/**
 * La scheda di un preventivo sul telefono (7/10/2026): risponde a "il cliente
 * l'ha firmato? cosa faccio adesso?".
 *
 * Prima, a 390px: il titolo gigante, il filo dei soldi, il ciclo di vita a
 * cinque pallini che andava a capo, una nota "si fa dal computer", le voci e,
 * in coda, il modulo del cliente e del tracciamento.
 * Qui:
 *   1. in grande, quanto vale la proposta; sotto, a che punto è la firma
 *      (mandato, aperto, firmato, diventato fattura), rosso solo se rifiutato;
 *   2. il cliente; la firma passo per passo, con il link da copiare; le voci;
 *      il messaggio al cliente e le note interne;
 *   3. in fondo il gesto che serve adesso, lo stesso del computer: mandarlo,
 *      rimandare il link, creare la fattura, aprirla;
 *   4. nel menu ⋯ in alto: copia il link, segna rifiutato.
 * Duplicare, stampare, eliminare e creare il progetto dal preventivo restano al
 * computer (sono procedure o non si annullano): lo dice una riga in fondo.
 * Stesse chiamate e stessi messaggi della pagina del computer.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { toast } from '../../stores/toast.store';
import { Icon, type IconName } from '../../components/ui/Icon';
import { ActionsMenu, type ActionItem } from '../../components/ui/ActionsMenu';
import { PageActions } from '../../components/layout/PageActions';
import { MCampo, MEmpty, MGroup, MHero, MPage, MRow, iniziali, leggibile } from '../../components/mobile/MobileKit';
import { fmtEur } from '../../lib/utils';
import { DueRighe, Foglio, Testo, euroEsatto, euroRiga, giornoBreve } from '../admin/shared/AdminMobile';

interface PreventivoPieno {
  id: string;
  number: string | null;
  title: string | null;
  introMessage: string | null;
  notes: string | null;
  status: string;
  issueDate: string | null;
  validUntil: string | null;
  subtotal: string; vat: string; total: string;
  sentAt: string | null;
  viewedAt: string | null;
  signedAt: string | null;
  signerName: string | null;
  rejectedAt: string | null;
  rejectionReason: string | null;
  convertedAt: string | null;
  convertedToInvoice: { id: string; number: string | null; status: string } | null;
  signatureToken: string | null;
  client: { id: string; legalName: string; vat: string | null };
  project: { id: string; code: string; name: string } | null;
  lines: Array<{ id: string; description: string; quantity: string; unitPrice: string; vatRate: string; order: number }>;
}

interface Contatto { id: string; fullName: string; email: string | null; isPrimary: boolean }
interface Invio { to: string; cc: string; subject: string; message: string }
type CampoInvio = keyof Invio;

const MESSAGGIO = 'Ciao,\n\ncome promesso ti mando il preventivo. Trovi tutto nel link qui sotto: puoi visualizzarlo e firmarlo direttamente online, in pochi secondi.\n\nFammi sapere se hai dubbi o serve qualche aggiustamento.\n\nUn saluto';

export function PreventivoMobile({ id }: { id: string }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: q, isLoading, isError } = useQuery({
    queryKey: ['quote', id],
    queryFn: () => api.get<PreventivoPieno>(`/quotes/${id}`),
    retry: false,
  });
  const [invio, setInvio] = useState<Invio | null>(null);
  const [campo, setCampo] = useState<CampoInvio | null>(null);
  const [bozza, setBozza] = useState('');
  // I contatti del cliente, per proporre la mail giusta (come la finestra del computer).
  const { data: contatti } = useQuery({
    queryKey: ['client-contacts', q?.client.id],
    queryFn: () => api.get<Contatto[]>(`/clients/${q!.client.id}/contacts`).catch(() => [] as Contatto[]),
    enabled: !!q && !!invio,
  });

  const rinfresca = () => { qc.invalidateQueries({ queryKey: ['quote', id] }); qc.invalidateQueries({ queryKey: ['quotes'] }); };
  const sendQuote = useMutation({
    mutationFn: (p: { to: string; cc?: string; subject: string; message: string }) => api.post(`/quotes/${id}/send`, p),
    onSuccess: () => { rinfresca(); setInvio(null); toast.ok('Preventivo inviato', 'Il cliente riceverà un\'email con il link per firmare.'); },
    onError: (e: any) => toast.alert('Invio fallito', e?.message ?? 'Riprova'),
  });
  const convert = useMutation({
    mutationFn: () => api.post<{ invoiceId: string }>(`/quotes/${id}/convert-to-invoice`),
    onSuccess: (res) => {
      rinfresca();
      qc.invalidateQueries({ queryKey: ['invoices'] });
      toast.ok('Convertito in fattura', 'Apro la nuova fattura…');
      navigate(`/invoices/${res.invoiceId}`);
    },
    onError: (e: any) => toast.alert('Errore', e?.message ?? 'Riprova'),
  });
  // Rifiuto reversibile: niente conferma, si annulla dal messaggio (come sul computer).
  const reject = useMutation({
    mutationFn: (v: { reason: string; prevStatus: string }) => api.post(`/quotes/${id}/reject`, { reason: v.reason }),
    onSuccess: (_r, v) => {
      rinfresca();
      toast.undo('Preventivo rifiutato', { onUndo: () => api.patch(`/quotes/${id}`, { status: v.prevStatus }).then(rinfresca) });
    },
    onError: (e: any) => toast.alert('Errore', e?.message ?? 'Riprova'),
  });

  if (isError) return <MPage><MEmpty>Preventivo non trovato: forse è stato eliminato, o il link è sbagliato.</MEmpty></MPage>;
  if (isLoading || !q) return <MPage><MEmpty>Carico il preventivo…</MEmpty></MPage>;

  const bozzaP = q.status === 'BOZZA';
  const mandato = q.status === 'INVIATO' || q.status === 'VISUALIZZATO';
  const firmato = q.status === 'ACCETTATO';
  const fatturato = q.status === 'CONVERTITO';
  const chiuso = q.status === 'RIFIUTATO' || q.status === 'SCADUTO';
  const linkFirma = q.signatureToken ? `${window.location.origin}/sign/quotes/${q.signatureToken}` : null;
  const copiaLink = () => {
    if (!linkFirma) return;
    navigator.clipboard.writeText(linkFirma)
      .then(() => toast.ok('Link copiato', 'Incollalo dove vuoi: WhatsApp, mail, messaggi.'))
      .catch(() => toast.alert('Link non copiato', 'Il browser non ha dato il permesso: riprova.'));
  };
  const apriInvio = () => setInvio({ to: '', cc: '', subject: q.title ?? `Preventivo ${q.number ?? ''}`.trim(), message: MESSAGGIO });
  const principale = contatti?.find((c) => c.isPrimary && c.email) ?? contatti?.find((c) => c.email);
  const destinatario = invio?.to || principale?.email || '';

  const stato = bozzaP ? 'bozza, non ancora mandata'
    : q.status === 'INVIATO' ? `mandato${q.sentAt ? ` il ${giornoBreve(q.sentAt)}` : ''}, non ancora aperto`
    : q.status === 'VISUALIZZATO' ? `aperto dal cliente${q.viewedAt ? ` il ${giornoBreve(q.viewedAt)}` : ''}, da firmare`
    : firmato ? `firmato${q.signedAt ? ` il ${giornoBreve(q.signedAt)}` : ''}${q.signerName ? ` da ${q.signerName}` : ''}`
    : fatturato ? `diventato la fattura ${q.convertedToInvoice?.number ?? ''}`.trim()
    : q.status === 'RIFIUTATO' ? <span className="is-alert">rifiutato{q.rejectionReason ? `: ${q.rejectionReason}` : ''}</span>
    : <span className="is-alert">scaduto senza firma</span>;

  let primary: { key: string; label: string; icon: IconName; onClick: () => void; pending?: boolean } | null = null;
  if (bozzaP) primary = { key: 'send', label: 'Manda al cliente', icon: 'send', onClick: apriInvio };
  else if (mandato) primary = { key: 'resend', label: 'Rimanda il link al cliente', icon: 'mail', onClick: apriInvio };
  else if (firmato) primary = { key: 'convert', label: 'Crea la fattura', icon: 'invoice', onClick: () => convert.mutate(), pending: convert.isPending };
  else if (fatturato && q.convertedToInvoice) primary = { key: 'open-invoice', label: 'Apri la fattura', icon: 'arrowRight', onClick: () => navigate(`/invoices/${q.convertedToInvoice!.id}`) };

  const menu: ActionItem[] = [
    { key: 'copy', label: 'Copia il link per la firma', icon: 'link', onClick: copiaLink, hidden: !linkFirma || bozzaP },
    { key: 'resend', label: 'Rimanda il link al cliente', icon: 'mail', onClick: apriInvio, hidden: !mandato },
    { key: 'convert', label: 'Crea la fattura', icon: 'invoice', onClick: () => convert.mutate(), hidden: !firmato },
    { key: 'reject', label: 'Segna come rifiutato', icon: 'x', onClick: () => reject.mutate({ reason: 'Rifiutato internamente', prevStatus: q.status }), hidden: firmato || fatturato || chiuso },
  ];

  return (
    <>
      <PageActions className="flex gap-2">
        <ActionsMenu variant="ghost" label="Azioni sul preventivo" items={menu} excludeKey={primary?.key}
          disabled={sendQuote.isPending || convert.isPending || reject.isPending} />
      </PageActions>

      <MPage>
        <h1 className="sr-only">{q.title || `Preventivo ${q.number ?? ''}`}</h1>
        <MHero
          label={q.title || `Proposta per ${leggibile(q.client.legalName)}`}
          unit="€"
          value={fmtEur(q.total)}
          sub={<>Preventivo {q.number ?? 'senza numero'} · {stato}</>}
        />

        <MGroup title="Il cliente">
          <MRow text={iniziali(q.client.legalName)} round title={leggibile(q.client.legalName)}
            sub={q.client.vat ? `P.IVA ${q.client.vat}` : undefined} onClick={() => navigate(`/clients/${q.client.id}`)} />
        </MGroup>

        <MGroup title="La firma">
          {q.validUntil && <MRow icon="calendar" title="Vale fino al" trail={giornoBreve(q.validUntil)} trailTone="mute" />}
          <MRow icon="send" title="Mandato al cliente" trail={q.sentAt ? giornoBreve(q.sentAt) : 'non ancora'} trailTone="mute" />
          {(q.viewedAt || mandato) && <MRow icon="eye" title="Aperto dal cliente" trail={q.viewedAt ? giornoBreve(q.viewedAt) : 'non ancora'} trailTone="mute" />}
          {q.signedAt && <MRow icon="check" title="Firmato" sub={q.signerName ?? undefined} trail={giornoBreve(q.signedAt)} trailTone="mute" />}
          {q.convertedToInvoice && <MRow icon="invoice" title="Diventato fattura" sub={q.convertedToInvoice.number ? `Fattura ${q.convertedToInvoice.number}` : undefined} onClick={() => navigate(`/invoices/${q.convertedToInvoice!.id}`)} />}
          {chiuso && <MRow icon="x" tone="alert" title={q.status === 'SCADUTO' ? 'Scaduto senza firma' : 'Rifiutato'} sub={q.rejectionReason ?? undefined} trail={q.rejectedAt ? giornoBreve(q.rejectedAt) : undefined} trailTone="mute" />}
          {linkFirma && !bozzaP && <MRow icon="link" title="Link per la firma" sub="tocca per copiarlo e mandarlo come vuoi" onClick={copiaLink} chevron={false} />}
        </MGroup>

        <MGroup title="Cosa contiene" count={q.lines.length || undefined}
          note={`Imponibile ${euroEsatto(Number(q.subtotal))} più IVA ${euroEsatto(Number(q.vat))}.`}>
          {q.lines.length === 0 ? <MEmpty>Nessuna voce.</MEmpty> : [...q.lines].sort((a, b) => a.order - b.order).map((l) => {
            const qty = Number(l.quantity) || 0;
            const prezzo = Number(l.unitPrice) || 0;
            const iva = Number(l.vatRate) || 0;
            return <MRow key={l.id} title={<DueRighe>{l.description}</DueRighe>} sub={`${qty} × ${euroEsatto(prezzo)} · IVA ${iva}%`} trail={euroRiga(qty * prezzo * (1 + iva / 100))} />;
          })}
        </MGroup>

        {q.introMessage && <MGroup title="Messaggio al cliente"><Testo>{q.introMessage}</Testo></MGroup>}
        {q.notes && <MGroup title="Note interne"><Testo>{q.notes}</Testo></MGroup>}
        {q.project && (
          <MGroup title="Il progetto">
            <MRow icon="cube" title={q.project.name} sub={`progetto ${q.project.code}`} onClick={() => navigate(`/projects/${q.project!.id}`)} />
          </MGroup>
        )}

        <div className="mk-group-note">
          {firmato && !q.project && !q.convertedToInvoice ? 'Il progetto dal preventivo firmato si crea dal computer. ' : ''}
          Duplicare, stampare ed eliminare il preventivo si fanno dal computer.
        </div>
      </MPage>

      {primary && (
        <div className="m-actionbar m-only">
          <button className="btn btn-primary" disabled={primary.pending} onClick={primary.onClick}>
            <Icon name={primary.icon} size={12} /> {primary.label}
          </button>
        </div>
      )}

      {/* Mandarlo: i campi si cambiano uno alla volta, non un modulo in fila. */}
      {invio && (
        <Foglio
          open={campo === null}
          onClose={() => setInvio(null)}
          title={bozzaP ? 'Manda il preventivo' : 'Rimanda il link'}
          primary={(
            <button className="btn btn-primary m-block" disabled={!destinatario || !invio.subject.trim() || sendQuote.isPending}
              onClick={() => sendQuote.mutate({ to: destinatario, cc: invio.cc.trim() || undefined, subject: invio.subject.trim(), message: invio.message.trim() })}>
              <Icon name="send" size={12} /> {sendQuote.isPending ? 'Invio…' : 'Manda il preventivo'}
            </button>
          )}
        >
          <div className="mk-list">
            <MRow title="A chi" trail={destinatario || 'da scrivere'} trailTone={destinatario ? 'mute' : 'alert'} onClick={() => { setBozza(destinatario); setCampo('to'); }} />
            <MRow title="In copia" trail={invio.cc || 'nessuno'} trailTone="mute" onClick={() => { setBozza(invio.cc); setCampo('cc'); }} />
            <MRow title="Oggetto" trail={invio.subject || 'da scrivere'} trailTone="mute" onClick={() => { setBozza(invio.subject); setCampo('subject'); }} />
            <MRow title="Messaggio" trail={invio.message === MESSAGGIO ? 'quello di sempre' : 'scritto da te'} trailTone="mute" onClick={() => { setBozza(invio.message); setCampo('message'); }} />
          </div>
          <div className="mk-group-note">Il cliente riceve una mail con il link per firmarlo online: è gratis e vale come firma.</div>
        </Foglio>
      )}
      <MCampo
        open={!!invio && campo !== null}
        onClose={() => setCampo(null)}
        titolo={campo === 'to' ? 'A chi lo mandi' : campo === 'cc' ? 'In copia' : campo === 'subject' ? 'Oggetto' : 'Messaggio'}
        nota={campo === 'to' && contatti && contatti.filter((c) => c.email).length > 1
          ? `Contatti del cliente: ${contatti.filter((c) => c.email).map((c) => `${c.fullName} (${c.email})`).join(', ')}.`
          : campo === 'message' ? 'Il link per firmare si aggiunge da solo, sotto il messaggio.' : undefined}
        campo={campo === 'message'
          ? <textarea className="inp" rows={7} autoFocus value={bozza} onChange={(e) => setBozza(e.target.value)} />
          : <input className="inp" autoFocus type={campo === 'to' || campo === 'cc' ? 'email' : 'text'} inputMode={campo === 'to' || campo === 'cc' ? 'email' : undefined} value={bozza} onChange={(e) => setBozza(e.target.value)} />}
        onSalva={() => { if (invio && campo) setInvio({ ...invio, [campo]: bozza }); setCampo(null); }}
      />
    </>
  );
}
